import { ElementStatus } from '@skop-app/lib-types'

import type { ReferenceInput, ReferenceMutationInput } from '@/types/reference'
import type { OrganizationScopeKind } from '@/utils/organization'

type ReferenceOwnership = {
  byUserID?: string
  organizationID?: string
  organizationMasterID?: string
}

type PersistOrganizationScopeLike = {
  scopeKind?: OrganizationScopeKind
  activeOrganizationId?: string
  activeOrganizationMasterId?: string
}

/**
 * Aligns persisted reference org fields with `useListReferences` filters:
 * - solo: user-scoped (no organization IDs)
 * - organization: active scope (not session ownership alone)
 * - unresolved: legacy fallback to ownership
 */
export const resolveReferencePersistOrganizationIds = ({
  ownership,
  organizationScope,
}: {
  ownership?: ReferenceOwnership | null
  organizationScope?: PersistOrganizationScopeLike | null
}): Pick<ReferenceInput, 'organizationID' | 'organizationMasterID'> => {
  const scopeKind = organizationScope?.scopeKind
  if (scopeKind === 'solo') {
    return {}
  }
  if (scopeKind === 'organization' && organizationScope) {
    const { activeOrganizationId, activeOrganizationMasterId } =
      organizationScope
    return {
      ...(activeOrganizationId ? { organizationID: activeOrganizationId } : {}),
      ...(activeOrganizationMasterId
        ? { organizationMasterID: activeOrganizationMasterId }
        : {}),
    }
  }
  return {
    ...(ownership?.organizationID && {
      organizationID: ownership.organizationID,
    }),
    ...(ownership?.organizationMasterID && {
      organizationMasterID: ownership.organizationMasterID,
    }),
  }
}

type GetReferenceCreationStatusParams = {
  currentStatus?: ReferenceInput['status']
  ownership?: ReferenceOwnership
  listOrganizationID?: string
  isAdmin?: boolean
  isSkopAdmin?: boolean
}

export const sanitizeReferenceInput = (
  input: ReferenceInput,
): ReferenceMutationInput => {
  const {
    id,
    byUserID,
    properties,
    organizationID,
    organizationMasterID,
    status,
  } = input

  return {
    id,
    byUserID,
    ...(properties ? { properties } : {}),
    ...(organizationID ? { organizationID } : {}),
    ...(organizationMasterID ? { organizationMasterID } : {}),
    ...(status ? { status } : {}),
  }
}

export const getReferenceCreationStatus = ({
  currentStatus,
  ownership,
  listOrganizationID = process.env.NEXT_PUBLIC_SKOP_ORGID,
  isAdmin = false,
  isSkopAdmin = false,
}: GetReferenceCreationStatusParams): ReferenceInput['status'] => {
  if (currentStatus) return currentStatus
  if (!ownership?.byUserID) return undefined

  const isOwnerOrgAdmin = Boolean(
    isAdmin &&
    ownership.organizationID &&
    listOrganizationID &&
    ownership.organizationID === listOrganizationID,
  )

  return isSkopAdmin || isOwnerOrgAdmin
    ? ElementStatus.ACCEPTED
    : ElementStatus.PENDING
}
